import React, {useEffect} from "react";
import {Button, Card, CardContent, Grid, Toolbar, Typography} from "@mui/material";
import {Field, Form} from "react-final-form";
import TextField from "../../../../../components/text_field";
import {pocPrimary, pageContentGap} from "../../../../../theme";
import { Alert } from '@mui/material';
import {getCatalogSyncHistories, utilTriggerWorkflowForPartner} from "../../../../../utils/utils";
import {identity} from "./setting_modifier";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import cronstrue from "cronstrue";
import EnhancedCircularLoader from "../../../../../components/EnhancedCircularLoader";
import moment from "moment";

interface ChannelProperty {
    name: string;
    partnerOnly?: boolean;
    allowNonMarketplace?: boolean;
    component?: (props: any) => React.ReactNode;
    [key: string]: any;
}

interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: string;
}

interface ChannelModifierProps {
    partners: any[];
    retailers: any[];
    attributes?: any[];
    properties?: ChannelProperty[];
    saveRow: (values: any) => Promise<any> | any;
    state: { settings?: any; [key: string]: any };
    profiles?: any[];
    groupedAttributes: { [key: string]: any };
    lastImports?: { [key: string]: any };
}

export default function ChannelModifier(props: ChannelModifierProps) {
    const {partners, retailers} = props;
    // Marketplace retailers are syndication channels; non-marketplace (billable) retailers only
    // get the mapping profile setting, so the general (all-channel) settings do not apply to them.
    const hasChannels = retailers.some(retailer => retailer.Marketplace);

    let properties: ChannelProperty[] = props.properties || [];

    const [error, setError] = React.useState<ErrorState>({open: false, errorHeader: "", errorMessage: ""});
    const [schedules, setSchedules] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [firingSync, setFiringSync] = React.useState(false);

    const fireSync = async (connectorId: any) => {
        setFiringSync(true);
        const response = await utilTriggerWorkflowForPartner(connectorId);
        if (!response.success) {
            setError({open: true, errorHeader: "Error sending manual sync", errorMessage: response.error});
        }
        setFiringSync(false);
    }

    useEffect(() => {
        const getSchedules = async () => {
            let response = await getCatalogSyncHistories();
            if(response.code !== 200) {
                setError({open: true, errorHeader: "Error getting schedules", errorMessage: response.error});
            }
            else {
                setSchedules(response.data);
            }
        }

        const init = async () => {
            setLoading(true);
            await getSchedules();
            setLoading(false);
        }

        init();
    }, []);

    return (
        <Grid container style={{gap: pageContentGap}}>
            <Grid container item size={12} style={{gap: pageContentGap, flexDirection: "column"}}>
                {hasChannels && (
                    <Card>
                        <Toolbar
                            className='lbtoolbar'
                            style={{background: pocPrimary}}
                        >
                            General Settings
                        </Toolbar>

                        <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                            <Typography variant={"body1"} style={{textAlign: "left"}}>
                                The settings configured in this section will be applied to all channels. If you would like
                                to configure settings on a per-channel basis, this can be done in the channel specific
                                space, below. Please note: a schedule is required in order to automatically list products.
                                Any settings applied on a per-channel basis will override what has been applied in General
                                Settings.
                            </Typography>
                        </Alert>

                        <CardContent>
                            <Form onSubmit={props.saveRow} initialValues={props.state.settings}
                                  render={({handleSubmit, pristine, hasValidationErrors, values, submitError}) => {
                                      return (
                                          <form onSubmit={handleSubmit}
                                                style={{display: 'flex', flexDirection: 'column', maxWidth: '900px'}}>
                                              {properties.filter(prop => !prop.partnerOnly).map(r => <div key={r.name} style={{display: 'flex'}}>
                                                  <Field style={{flex: 1}} parse={identity} component={TextField as any} {...r}/>
                                              </div>)}
                                              {submitError && <Alert severity="error" style={{marginTop: '1em'}}>{submitError}</Alert>}
                                              <div style={{
                                                  textAlign: 'left', marginBottom: '.5em',
                                                  marginTop: '0.5em'
                                              }}>
                                                  {/* hasValidationErrors, not invalid: final-form folds submit errors into
        `invalid`, so a failed save would otherwise grey out Save and block the retry. */}
    <Button variant="contained" color="primary" disabled={pristine || hasValidationErrors} onClick={handleSubmit}>Save</Button>
                                              </div>
                                          </form>
                                      );
                                  }}/>
                        </CardContent>
                    </Card>
                )}

                <>
                    {retailers.map((retailer, index) => {
                        const isChannel = retailer.Marketplace === true;
                        const profiles = props.profiles.filter((p: any) => p.Partner === retailer.Id && p.Shared !== undefined);
                        const schedule = schedules.find(s => s.CoId === retailer.Id);
                        return (
                            <Card key={index}>
                                <Toolbar
                                    className='lbtoolbar'
                                    style={{background: pocPrimary}}
                                >
                                    {partners.find(p => p.Id === retailer.Id)?.CompanyName ?? "Unknown"} Settings
                                </Toolbar>

                                {isChannel && (
                                    <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                                        <Typography variant={"body1"} style={{textAlign: "left"}}>
                                            These settings will override the general settings configured. Please note: a
                                            schedule is required in order to automatically list products.
                                        </Typography>
                                    </Alert>
                                )}

                                <CardContent style={{display: "flex", flexDirection: "column", gap: "20px"}}>
                                    {isChannel && (
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Grid container style={{flexDirection: "column", textAlign: "left", width: "100%"}}>
                                                <Typography style={{color: "black", fontWeight: "bold", fontSize: "1rem"}}>
                                                    Scheduling
                                                </Typography>

                                                <Typography style={{color: "#c0c0c0", paddingBottom: "10px"}}>
                                                    Below is the schedule for frequency of when products are being sent to {partners.find(p => p.Id === retailer.Id)?.CompanyName ?? "Unknown"}.
                                                </Typography>
                                            </Grid>

                                            <Grid style={{width: "100%"}}>
                                                {loading ? (
                                                    <Grid style={{padding: "20px"}}>
                                                        <EnhancedCircularLoader styles={{width: "40px", height: "40px", margin: "10px"}}/>
                                                    </Grid>
                                                ) : (
                                                    <Typography style={{padding: "5px", textAlign: "left"}}>
                                                        <b>Runs:{" "}</b>
                                                        {schedule?.Schedule
                                                            ? cronstrue.toString(schedules.find(s => s.CoId === retailer.Id)?.Schedule ?? "* * * * *")
                                                            : "No schedule has been set yet."
                                                        }
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid container style={{flexDirection: "row", padding: "5px", justifyContent: "flex-start", width: "100%"}}>
                                                <Button
                                                    variant={"contained"}
                                                    color={"primary"}
                                                    disabled={!schedule?.Schedule || firingSync}
                                                    onClick={() => fireSync(retailer.Id)}
                                                >
                                                    {firingSync ? (
                                                        <EnhancedCircularLoader styles={{height: "20px", width: "20px"}}/>
                                                    ) : "Go Faster"}
                                                </Button>
                                            </Grid>

                                            <Grid container style={{flexDirection: "column", textAlign: "left", padding: "5px", width: "100%"}}>
                                                <Typography style={{color: "black", fontWeight: "bold"}}>
                                                    Last Import
                                                </Typography>

                                                <Typography style={{color: "#c0c0c0", paddingBottom: "10px"}}>
                                                    {
                                                        props.lastImports[retailer.Id]
                                                            ?
                                                            <>
                                                                {props.lastImports[retailer.Id]?.Description ?? "Unknown"} on {moment(props.lastImports[retailer.Id]?.CreateDate).format("MM/DD/YYYY")}
                                                            </>
                                                            : "No import has been made yet."
                                                    }
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )}

                                    <Form
                                        onSubmit={(e: any) => {
                                            const params = {
                                                ...e,
                                                AccountNumber: retailer.Id,
                                            };
                                            // Returned, not dropped: this is the
                                            // submit error surfaced below.
                                            return props.saveRow(params);
                                        }}
                                        initialValues={props.state.settings.PartnerProperties?.find((p: any) => p.PartnerCoId === retailer.Id) ?? {}}
                                        render={({handleSubmit, pristine, hasValidationErrors, values, submitError}) => {
                                            return (
                                                <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', maxWidth: '900px', gap: "20px"}}>
                                                    {
                                                        properties.filter(r => isChannel || r.allowNonMarketplace).map(r => (
                                                            <div key={r.name} style={{display: 'flex'}}>
                                                                <Field
                                                                    style={{flex: 1}}
                                                                    parse={identity}
                                                                    {...r}
                                                                    component={(innerProps: any) => {
                                                                        return (
                                                                            <>
                                                                                {
                                                                                    r.name === "POCAutoIgnoreUpdatedFields"
                                                                                        ? r.component!({
                                                                                            ...innerProps,
                                                                                            options: (props.groupedAttributes[retailer.Id] ?? []),
                                                                                        })
                                                                                        : r.component!({...innerProps, profiles, lastImport: props.lastImports[retailer.Id], retailerId: retailer.Id})
                                                                                }
                                                                            </>
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        ))
                                                    }
                                                    {submitError && <Alert severity="error">{submitError}</Alert>}
                                                    <div style={{
                                                        textAlign: 'left',
                                                        display: "flex",
                                                        gap: "20px",
                                                    }}>
                                                        {/* hasValidationErrors, not invalid: final-form folds submit errors into
    `invalid`, so a failed save would otherwise grey out Save and block the retry. */}
<Button variant="contained" color="primary" disabled={pristine || hasValidationErrors} onClick={handleSubmit}>Save</Button>
                                                    </div>
                                                </form>
                                            );
                                        }}/>
                                </CardContent>
                            </Card>
                        );
                    })}
                </>
            </Grid>

            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </Grid>
    );
}
